import React from "react";
import "./cartAudits.scss";
import { ICartAudit } from "api/rpc/2024-04/facilityAdmin/cart/cart";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { faArrowRight, faCircleExclamation, faCirclePlus, faTrashCan } from "@fortawesome/pro-regular-svg-icons";
import moment from "moment";
import { valueToString } from "helpers/Helpers";
import { useTranslation } from "react-i18next";
import useCurrencyFacility from "hooks/useCurrency/useCurrencyFacility";
import { useAppSelector } from "hooks/redux";

interface IProps {
    audits: Array<ICartAudit>;
}

export default function CartAudits(props: IProps) {
    const { audits } = props;
    const { t } = useTranslation();
    const { displayCurrency } = useCurrencyFacility();
    const { facilityStore } = useAppSelector(store => store);

    function formatValue(value: any, type: "previous" | "new", key: string) {
        if (type === "previous") {
            if (value === null) {
                return null;
            }
        } else {
            if (value === null) {
                return t("Removed");
            }
        }

        if (typeof value === "boolean") {
            if (value === true) {
                return t("On");
            } else if (value === false) {
                return t("Off");
            }
        }

        switch (key) {
            case "price":
            case "total_tip":
            case "discount_amount":
                return displayCurrency(value as number, facilityStore?.facility?.default_currency_code);
            case "modifiers":
                return value?.map((modifier: Record<string, any>) => ` ${modifier?.title as string} (${displayCurrency(modifier?.price as number, facilityStore?.facility?.default_currency_code)})`) as string;
            default:
                return value as string;
        }
    }

    return (
        <div>
            {audits?.map((audit, index, allAudits) => {
                return (
                    <div key={index} className="ui-cart-audit-container">
                        <div className="ui-cart-audit-status-container">
                            <div className={classNames("ui-cart-audit-status", { [`ui-cart-audit-status-${audit?.action}`]: audit?.action })}>
                                <FontAwesomeIcon icon={getIcon(audit.action)} />
                            </div>
                            {index + 1 !== allAudits?.length ? (
                                <div className="ui-cart-audit-status-line"></div>
                            ) : null}
                        </div>
                        <div className="ui-cart-audit-info-container">
                            <p className="ui-cart-audit-primary">{audit?.title}</p>
                            <div className="ui-cart-audit-primary">
                                {audit?.new_value && audit?.previous_value ? Object.keys(audit?.previous_value)?.map((prevValueKey, prevValueIndex) => {
                                    return <p key={prevValueIndex} className="ui-cart-audit-secondary">{`${valueToString(prevValueKey)}:`} {audit?.previous_value[prevValueKey] != null ? <>{`${formatValue(audit?.previous_value[prevValueKey], "previous", prevValueKey)}`} <FontAwesomeIcon icon={faArrowRight} /> </> : null} {formatValue(audit?.new_value[prevValueKey], "new", prevValueKey)}</p>;
                                }) : audit?.new_value ? Object.keys(audit?.new_value)?.map((newValueKey, newValueIndex) => {
                                    return <p key={newValueIndex} className="ui-cart-audit-secondary">{`${valueToString(newValueKey)}: ${formatValue(audit?.new_value[newValueKey], "new", newValueKey)}`}</p>;
                                }) : null}
                            </div>
                            <p className="ui-cart-audit-tertiary">{audit?.description} • {moment.utc(audit?.created_at)?.local()?.format("LLL")}</p>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}


function getIcon(action: "create" | "update" | "delete") {
    switch (action) {
        case "create":
            return faCirclePlus;
        case "update":
            return faCircleExclamation;
        case "delete":
            return faTrashCan;
        default:
            return faCirclePlus;
    }
}